import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { useApolloClient } from '@apollo/client/react';

import { FontWeight } from '@clutter/clean';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { saveStorageDraftAndNotify } from '@root/components/checkout/utilities/drafts';
import { ServiceEnum } from '@root/resources/types/service';
import { email as emailValidator } from '@root/resources/validator';

import { useClientDataContext } from '@shared/client_data_context';
import { useCreateLead } from '@shared/lead_form';

import { GQLError } from '@utils/gql_errors';
import { titleize } from '@utils/text';

import closeIcon from '@images/checkout/challenger/close.svg';
import emailSavedIcon from '@images/checkout/challenger/email_saved.svg';

import { useModalFocus } from './hooks/use_modal_focus';
import { TABLET_UP as DESKTOP, MOBILE } from './media';
import { CHALLENGER as T } from './tokens';

/** Figma 908:26753 — desktop success modal shell. Inner copy fills padded width. */
const SUCCESS_MODAL_DESKTOP_WIDTH_PX = 420;

/** Figma 931:46566 — mobile success modal shell. */
const SUCCESS_MODAL_MOBILE_WIDTH_PX = 370;

/** Figma 908:26753 / 931:46566 — shared success spacing. */
const SUCCESS_ICON_TO_COPY_GAP_PX = 24;
const SUCCESS_TITLE_TO_COPY_GAP_PX = 16;
const SUCCESS_CONTENT_TO_ACTION_GAP_PX = 32;

/**
 * Figma save-progress workflow:
 * 707:7719 empty · 879:13038 filled · 882:14064 error · 908:26753 success
 */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${T.space[16]};
  box-sizing: border-box;
  background: rgba(4, 7, 17, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

/** Figma 707:7719 / 908:26753 — 420px shell, 24px pad, 376px text / 372px actions. */
const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[16]};
  width: 100%;
  max-width: 420px;
  padding: ${T.space[24]} ${T.space[24]} ${T.space[16]} ${T.space[24]};
  box-sizing: border-box;
  border-radius: 16px;
  background: ${T.color.bgPage};
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};

  ${DESKTOP} {
    width: ${SUCCESS_MODAL_DESKTOP_WIDTH_PX}px;
    max-width: ${SUCCESS_MODAL_DESKTOP_WIDTH_PX}px;
  }
`;

const SuccessDialog = styled(Dialog)`
  align-items: flex-start;
  gap: ${SUCCESS_CONTENT_TO_ACTION_GAP_PX}px;

  ${DESKTOP} {
    width: ${SUCCESS_MODAL_DESKTOP_WIDTH_PX}px;
    max-width: ${SUCCESS_MODAL_DESKTOP_WIDTH_PX}px;
    padding: ${T.space[24]} ${T.space[24]} ${T.space[16]} ${T.space[24]};
  }

  ${MOBILE} {
    width: ${SUCCESS_MODAL_MOBILE_WIDTH_PX}px;
    max-width: calc(100vw - 32px);
    padding: ${T.space[16]};
  }
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: ${T.space[16]};
`;

const Title = styled.h2`
  margin: 0;
  flex: 1;
  font-size: 20px;
  font-weight: ${FontWeight.Medium};
  line-height: 23px;
  color: ${T.color.textPrimary};
`;

const SuccessTitle = styled(Title)`
  flex: none;
  width: 100%;
  text-align: center;
`;

/** Close Icon 24×24; vector leaf 14×14. */
const CloseButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  img {
    display: block;
    width: 14px;
    height: 14px;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  width: 100%;
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textPrimary};
`;

/** Figma 908:26758 — Body/Medium 400; wraps naturally in the padded modal. */
const SuccessCopy = styled.p`
  margin: 0;
  width: 100%;
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textPrimary};
  text-align: center;
  overflow-wrap: anywhere;
`;

const EmailEmphasis = styled.span`
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  overflow-wrap: anywhere;
`;

const FormBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
  width: 100%;
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const EmailInput = styled.input<{ $invalid?: boolean }>`
  appearance: none;
  width: 100%;
  height: 56px;
  padding: ${T.space[4]} ${T.space[4]} ${T.space[4]} ${T.space[16]};
  box-sizing: border-box;
  border: 1px solid ${T.color.borderDefault};
  border-radius: ${T.radius.sm};
  background: ${T.color.bgPrimary};
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textPrimary};
  outline: none;

  &::placeholder {
    color: ${T.color.textTertiary};
  }

  &:focus {
    border-color: ${({ $invalid }) =>
      $invalid ? T.color.textError : T.color.borderBrand};
  }
`;

const FieldError = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textError};
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  padding: 0 ${T.space[24]};
  border: none;
  border-radius: ${T.radius.sm};
  background: ${({ disabled }) =>
    disabled ? T.color.bgButtonDisabled : T.color.textBrand};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textInverse};
  white-space: nowrap;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`;

const SuccessDoneButton = styled(PrimaryButton)`
  width: 100%;
`;

const SuccessBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SUCCESS_ICON_TO_COPY_GAP_PX}px;
  width: 100%;
`;

/** Figma 909:27225 / 931:46760 — title + copy stack. */
const SuccessCopyBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${SUCCESS_TITLE_TO_COPY_GAP_PX}px;
  width: 100%;
`;

/** Figma 908:26755 / 931:46761 — centered title row. */
const SuccessHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

/**
 * Figma 908:26753 — 88×88 icon container (white + teal 25%), icon box 48,
 * leaf 42×36 (#08AAA8).
 */
const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  padding: 4px;
  box-sizing: border-box;
  border-radius: ${T.radius.md};
  background-color: ${T.color.bgPrimary};
  background-image: linear-gradient(
    rgba(11, 122, 122, 0.25),
    rgba(11, 122, 122, 0.25)
  );
  flex-shrink: 0;
`;

const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;

  img {
    display: block;
    width: 42px;
    height: 36px;
  }
`;

type Step = 'form' | 'success';

const hasFullName = (name?: string): boolean =>
  !!name && name.trim().split(/\s+/).length >= 2;

/**
 * Page 1 save-progress only asks for email, but leadCreate still requires a
 * two-token name. Derive one from the address when contact is not collected yet.
 */
const deriveSaveProgressLeadName = (email: string): string => {
  const localPart = email.split('@')[0]?.trim() ?? '';
  const tokens = localPart
    .replace(/[._+-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  if (tokens.length >= 2) {
    return tokens.map((part) => titleize(part)).join(' ');
  }

  return `${titleize(tokens[0] || 'Clutter')} Customer`;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const SaveProgressModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const client = useApolloClient();
  const createLead = useCreateLead();
  const {
    flowVersion,
    pricingSet,
    flowState: { values, onChange },
  } = useStorageCheckoutContext();
  const {
    data: {
      lead: { token: clientLeadToken, email: clientLeadEmail },
    },
  } = useClientDataContext();

  const existingEmail = values.email || clientLeadEmail || '';
  const [email, setEmail] = useState(existingEmail);
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<Step>('form');
  const [savedEmail, setSavedEmail] = useState('');

  const wasOpenRef = useRef(false);
  const submittingRef = useRef(false);
  const formDialogRef = useRef<HTMLDivElement>(null);
  const successDialogRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useModalFocus(isOpen && step === 'form', formDialogRef, emailInputRef);
  useModalFocus(isOpen && step === 'success', successDialogRef);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    if (submittingRef.current) return;
    setEmail(existingEmail);
    setError(undefined);
    setSubmitting(false);
    setStep('form');
    setSavedEmail('');
  }, [isOpen, existingEmail]);

  const requestClose = () => {
    if (submittingRef.current) return;
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submittingRef.current) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleSave = async () => {
    if (submittingRef.current) return;
    const trimmed = email.trim();
    if (!emailValidator(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!pricingSet) {
      setError('Unable to save your progress. Please try again.');
      return;
    }

    setError(undefined);
    submittingRef.current = true;
    setSubmitting(true);

    try {
      let leadToken = values.leadToken || clientLeadToken;
      const emailChanged =
        !existingEmail ||
        existingEmail.trim().toLowerCase() !== trimmed.toLowerCase();

      if (!leadToken || emailChanged) {
        const leadName = hasFullName(values.name)
          ? values.name!.trim()
          : deriveSaveProgressLeadName(trimmed);
        const { token } = await createLead({
          leadAttributes: {
            email: trimmed,
            zip: values.zip,
            name: leadName,
            ...(values.phone ? { phone: values.phone } : {}),
          },
          serviceContext: ServiceEnum.DoorToDoor,
        });
        leadToken = token;
        onChange('leadToken', token);
        onChange('email', trimmed);
      }

      if (!leadToken) {
        setError('Unable to save your progress. Please try again.');
        return;
      }

      const result = await saveStorageDraftAndNotify(
        client,
        { ...values, leadToken, email: trimmed },
        leadToken,
        pricingSet,
        flowVersion,
      );

      if (!result.ok) {
        setError(result.error);
        return;
      }

      setSavedEmail(trimmed);
      setStep('success');
    } catch (err) {
      if (err instanceof GQLError) {
        setError(
          err.fullError.errorMessage ||
            'Unable to save your progress. Please try again.',
        );
      } else {
        setError('Unable to save your progress. Please try again.');
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const onOverlayClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (event.currentTarget === event.target) requestClose();
  };

  if (step === 'success') {
    return (
      <Overlay onClick={onOverlayClick} role="presentation">
        <SuccessDialog
          ref={successDialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="challenger-save-progress-success-title"
          onClick={(event) => event.stopPropagation()}
        >
          <SuccessBody>
            <IconContainer aria-hidden>
              <IconBox>
                <img src={emailSavedIcon} alt="" width={42} height={36} />
              </IconBox>
            </IconContainer>
            <SuccessCopyBlock>
              <SuccessHeaderRow>
                <SuccessTitle id="challenger-save-progress-success-title">
                  Progress saved
                </SuccessTitle>
              </SuccessHeaderRow>
              <SuccessCopy>
                We sent a link to email{' '}
                <EmailEmphasis>{savedEmail}</EmailEmphasis>
                {' so you can pick up where you left off.'}
              </SuccessCopy>
            </SuccessCopyBlock>
          </SuccessBody>
          <SuccessDoneButton type="button" onClick={onClose}>
            Done
          </SuccessDoneButton>
        </SuccessDialog>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={onOverlayClick} role="presentation">
      <Dialog
        ref={formDialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenger-save-progress-title"
        onClick={(event) => event.stopPropagation()}
      >
        <HeaderBlock>
          <HeaderRow>
            <Title id="challenger-save-progress-title">
              Do you want to save progress?
            </Title>
            <CloseButton
              type="button"
              aria-label="Close"
              onClick={requestClose}
            >
              <img src={closeIcon} alt="" width={14} height={14} />
            </CloseButton>
          </HeaderRow>
          <Subtitle>
            No need to start over. We’ll save your progress and send it to your
            email.
          </Subtitle>
        </HeaderBlock>

        <FormBlock>
          <FieldWrap>
            <EmailInput
              ref={emailInputRef}
              type="email"
              autoComplete="email"
              placeholder="Enter your email"
              value={email}
              $invalid={!!error}
              aria-invalid={!!error}
              aria-describedby={error ? 'save-progress-email-error' : undefined}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) setError(undefined);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleSave();
                }
              }}
            />
            {error && (
              <FieldError id="save-progress-email-error">{error}</FieldError>
            )}
          </FieldWrap>
          <PrimaryButton
            type="button"
            disabled={submitting}
            onClick={() => void handleSave()}
          >
            {submitting ? 'Saving…' : 'Save progress'}
          </PrimaryButton>
        </FormBlock>
      </Dialog>
    </Overlay>
  );
};
